import { useEffect, useRef, useState } from 'react';
import { staffApi, formatApiError } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { CameraScanBox } from '@/components/shared/CameraScanBox';
import { useScanMode } from '@/hooks/useScanMode';
import { toast } from 'sonner';
import { QrCode, CheckCircle2, ScanLine, Camera } from 'lucide-react';

const QTY_OPTIONS = [1, 2, 3, 4, 5];

export default function QRScanner() {
  const [step, setStep] = useState('scan'); // scan -> review -> done
  const [scanSession, setScanSession] = useState(0);
  const [scanMode, setScanMode] = useScanMode();
  const [customer, setCustomer] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [customQty, setCustomQty] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [result, setResult] = useState(null);
  const handledRef = useRef(false);
  const hardwareInputRef = useRef(null);

  useEffect(() => {
    if (step === 'scan' && scanMode === 'SCANNER') {
      handledRef.current = false;
      hardwareInputRef.current?.focus();
    }
  }, [step, scanMode, scanSession]);

  const restartScanning = () => setScanSession((s) => s + 1);

  const handleHardwareScanKeyDown = async (e) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    const token = e.target.value.trim();
    e.target.value = '';
    if (!token || handledRef.current) return;
    handledRef.current = true;
    handleScan(token);
  };

  const handleScan = async (token) => {
    try {
      const { data } = await staffApi.post('/qr/scan', { token });
      setCustomer(data.customer);
      setSubscription(data.subscription);
      setStep('review');
    } catch (err) {
      toast.error(formatApiError(err));
      restartScanning();
    }
  };

  const finalQuantity = () => (quantity === 'custom' ? parseInt(customQty) || 0 : quantity);

  const confirmRedeem = async () => {
    if (!subscription) return;
    setConfirming(true);
    try {
      const { data } = await staffApi.post('/redemptions', {
        subscriptionId: subscription.id,
        quantity: finalQuantity(),
        method: 'QR',
      });
      setResult(data);
      setStep('done');
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setConfirming(false);
    }
  };

  const reset = () => {
    setCustomer(null);
    setSubscription(null);
    setQuantity(1);
    setCustomQty('');
    setResult(null);
    setStep('scan');
    restartScanning();
  };

  return (
    <div className="max-w-lg mx-auto space-y-6">
      <h1 className="font-heading text-2xl font-700 text-charcoal text-center">QR Scanner</h1>

      {step === 'scan' && (
        <div className="bg-white rounded-2xl border border-border/40 overflow-hidden">
          <div className="p-3 flex items-center justify-center gap-1 border-b border-border/40" data-testid="scan-mode-toggle">
            <button
              data-testid="scan-mode-camera-btn"
              onClick={() => setScanMode('CAMERA')}
              className={`flex items-center gap-1.5 px-4 py-1.5 rounded-full text-sm font-medium transition-colors duration-150 ${
                scanMode === 'CAMERA' ? 'bg-saffron text-white' : 'text-muted-ink hover:bg-bone'
              }`}
            >
              <Camera size={14} /> Camera
            </button>
            <button
              data-testid="scan-mode-scanner-btn"
              onClick={() => setScanMode('SCANNER')}
              className={`flex items-center gap-1.5 px-4 py-1.5 rounded-full text-sm font-medium transition-colors duration-150 ${
                scanMode === 'SCANNER' ? 'bg-saffron text-white' : 'text-muted-ink hover:bg-bone'
              }`}
            >
              <ScanLine size={14} /> Handheld Scanner
            </button>
          </div>

          {scanMode === 'CAMERA' ? (
            <>
              <CameraScanBox onScan={handleScan} scanSession={scanSession} />
              <div className="p-4 flex items-center gap-2 text-sm text-muted-ink">
                <QrCode size={16} /> Point the camera at the customer's THE TIFFIN QR code
              </div>
            </>
          ) : (
            <div className="p-8 flex flex-col items-center gap-3 text-center">
              <ScanLine size={40} className="text-saffron" />
              <p className="text-sm text-muted-ink">Ready — scan the customer's QR code with your handheld scanner</p>
              <input
                ref={hardwareInputRef}
                autoFocus
                data-testid="hardware-scanner-input"
                onKeyDown={handleHardwareScanKeyDown}
                className="opacity-0 h-1 w-1 absolute"
              />
            </div>
          )}
        </div>
      )}

      {step === 'review' && customer && (
        <div className="bg-white rounded-2xl border border-border/40 p-6 space-y-5" data-testid="scan-review-panel">
          <div>
            <p className="font-heading text-xl font-semibold text-charcoal" data-testid="scan-customer-name">{customer.name}</p>
            <p className="text-sm text-muted-ink">
              {customer.phone}{subscription ? ` · ${subscription.plan.name}` : ''}
            </p>
          </div>

          {subscription ? (
            <>
              <div className="flex justify-between bg-bone rounded-xl p-4">
                <span className="text-sm text-muted-ink">Remaining tiffins</span>
                <span className="font-heading text-2xl font-700 text-charcoal" data-testid="scan-remaining-balance">
                  {subscription.remainingCredits}
                </span>
              </div>

              <div>
                <p className="text-sm font-medium text-charcoal mb-2">How many tiffins are being collected?</p>
                <div className="grid grid-cols-3 gap-2">
                  {QTY_OPTIONS.map((n) => (
                    <button
                      key={n}
                      data-testid={`qty-option-${n}`}
                      onClick={() => setQuantity(n)}
                      className={`h-12 rounded-xl border text-lg font-semibold transition-colors duration-150 ${
                        quantity === n ? 'bg-saffron text-white border-saffron' : 'border-border/40 text-charcoal hover:bg-bone'
                      }`}
                    >
                      {n}
                    </button>
                  ))}
                  <button
                    data-testid="qty-option-custom"
                    onClick={() => setQuantity('custom')}
                    className={`h-12 rounded-xl border text-sm font-semibold transition-colors duration-150 ${
                      quantity === 'custom' ? 'bg-saffron text-white border-saffron' : 'border-border/40 text-charcoal hover:bg-bone'
                    }`}
                  >
                    Custom
                  </button>
                </div>
                {quantity === 'custom' && (
                  <input
                    type="number"
                    min={1}
                    data-testid="qty-custom-input"
                    value={customQty}
                    onChange={(e) => setCustomQty(e.target.value)}
                    className="mt-2 w-full h-12 rounded-xl border border-border/40 px-4 text-lg"
                    placeholder="Enter quantity"
                  />
                )}
              </div>

              <div className="flex gap-3">
                <Button variant="outline" className="flex-1 h-12" data-testid="scan-cancel-btn" onClick={reset}>
                  Cancel
                </Button>
                <Button
                  className="flex-1 h-12 bg-saffron hover:bg-saffron-hover text-base"
                  data-testid="scan-confirm-deduct-btn"
                  disabled={confirming || finalQuantity() < 1 || finalQuantity() > subscription.remainingCredits}
                  onClick={confirmRedeem}
                >
                  {confirming ? 'Deducting…' : `Confirm & Deduct ${finalQuantity() || ''}`}
                </Button>
              </div>
            </>
          ) : (
            <>
              <p className="text-sm text-destructive bg-red-50 rounded-xl p-4" data-testid="scan-no-subscription-message">
                This customer has no active subscription, so no tiffins can be deducted right now.
              </p>
              <Button variant="outline" className="w-full h-12" data-testid="scan-cancel-btn" onClick={reset}>
                Scan Next Customer
              </Button>
            </>
          )}
        </div>
      )}

      {step === 'done' && result && (
        <div className="bg-white rounded-2xl border border-border/40 p-8 text-center space-y-4" data-testid="scan-success-panel">
          <CheckCircle2 size={48} className="text-coriander mx-auto" />
          <p className="font-heading text-xl font-semibold text-charcoal">Pickup confirmed</p>
          <p className="text-sm text-muted-ink">New remaining balance</p>
          <p className="font-heading text-4xl font-900 text-charcoal" data-testid="new-remaining-balance">
            {result.subscription.remainingCredits}
          </p>
          <Button className="bg-saffron hover:bg-saffron-hover" data-testid="scan-next-btn" onClick={reset}>
            Scan Next Customer
          </Button>
        </div>
      )}
    </div>
  );
}
